import { memo, useCallback, useMemo, useState } from 'react';

import uniq from 'lodash-es/uniq';
import { PlusIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CollapsibleSection } from '@/components/atoms/CollapsibleSection/CollapsibleSection';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useConversationContext } from '@/hooks/useConversationContext';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { MAX_CONVERSATION_TAGS } from '@/util/constants';
import { newTagSchema } from '@/util/validation/schemas/dialog-schemas';

const truncateText = (text: string, maxLength = 20) =>
  text.length <= maxLength ? text : `${text.slice(0, maxLength)}...`;

export const ContextTagsSection = memo(() => {
  const { t } = useTranslation();
  const { tags, addTag, removeTag } = useConversationContext();
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const availableTags = useMemo(
    () => desk?.metadata?.conversation_tags?.filter((tag) => !tags.includes(tag)) ?? [],
    [desk?.metadata?.conversation_tags, tags]
  );

  const filteredTags = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return availableTags;
    }
    return availableTags.filter((tag) => tag.toLowerCase().includes(term));
  }, [availableTags, searchTerm]);

  const trimmed = searchTerm.trim();
  const hasResults = filteredTags.length > 0;
  const showCreateAction =
    !!trimmed &&
    newTagSchema.isValidSync({ tag: trimmed }) &&
    !availableTags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase()) &&
    !tags.includes(trimmed);

  const handleAdd = useCallback(
    (value: string) => {
      addTag(value, {
        onSuccess: () => {
          setSearchTerm('');
        },
      });
    },
    [addTag]
  );

  const handleCreateNew = useCallback(() => {
    if (showCreateAction) {
      handleAdd(trimmed);
    }
  }, [showCreateAction, handleAdd, trimmed]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  }, []);

  const isAddDisabled = !canWrite || tags.length >= MAX_CONVERSATION_TAGS;

  const handleAddTagButtonClick = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  const addTagButton = (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <InfoTooltip content={open ? undefined : t('common.add_tags')}>
        <PopoverTrigger asChild>
          <Button
            variant="link"
            size="icon-xss"
            type="button"
            aria-label={t('common.add_tags')}
            disabled={isAddDisabled}
            className="h-full w-full rounded opacity-0 transition-opacity group-hover:opacity-100 hover:bg-stroke hover:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed [&_svg]:text-accent"
            onClick={handleAddTagButtonClick}
          >
            <PlusIcon />
          </Button>
        </PopoverTrigger>
      </InfoTooltip>
      <PopoverContent className="z-1300 w-60 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t(
              availableTags.length > 0 ? 'common.search_or_type_to_add' : 'common.type_to_add'
            )}
            value={searchTerm}
            onValueChange={(next) => setSearchTerm(next.replace(/\s+/g, '_'))}
          />
          <CommandList id="autocomplete-list" className="scroll-py-1">
            {hasResults && (
              <CommandGroup
                heading={t('dialog.tags.auto_complete_group_by')}
                className="**:[[cmdk-group-heading]]:sticky **:[[cmdk-group-heading]]:top-0 **:[[cmdk-group-heading]]:z-10"
              >
                {filteredTags.map((tag) => (
                  <CommandItem
                    key={tag}
                    value={tag}
                    onSelect={() => handleAdd(tag)}
                    className="flex items-center justify-between"
                  >
                    <span>{tag}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!hasResults && trimmed && !showCreateAction && (
              <CommandEmpty>{t('common.no_results')}</CommandEmpty>
            )}
            {showCreateAction && (
              <CommandGroup className={hasResults ? 'border-t' : undefined}>
                <CommandItem
                  value={`__add__${trimmed}`}
                  onSelect={handleCreateNew}
                  className="flex items-center gap-2 text-foreground"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>{t('common.add_new', { 0: truncateText(trimmed) })}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  return (
    <section>
      <CollapsibleSection
        title={t('common.tags')}
        showBorder={false}
        rightIcon={addTagButton}
        storageKey="humanChat.sidebar.tags"
      >
        <div className="flex flex-wrap items-center gap-1 px-1.5 pb-3">
          {tags.length === 0 && (
            <div className="flex items-center gap-1 text-sm text-foreground">
              <span>{t('common.no_tags_added')}.</span>
              <Button
                variant="link-secondary"
                size="sm"
                type="button"
                disabled={isAddDisabled}
                className="h-auto px-0 py-0 text-sm text-informational [&>span]:px-0"
                onClick={() => setOpen(true)}
              >
                {t('common.add')}
              </Button>
            </div>
          )}
          {uniq(tags).map((tag) => (
            <Badge
              key={tag}
              type="outline"
              flavour="neutral"
              rounded={false}
              size="xs"
              className="bg-background"
              trailingIcon={
                <button
                  type="button"
                  aria-label={t('common.delete')}
                  onClick={() => removeTag(tag)}
                  className="flex items-center justify-center rounded-sm text-foreground-muted hover:bg-bg-muted-50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <XIcon className="size-3" />
                </button>
              }
            >
              {tag}
            </Badge>
          ))}
        </div>
      </CollapsibleSection>
    </section>
  );
});

ContextTagsSection.displayName = 'ContextTagsSection';
